import {
  DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useCallbackRef,
  usePersistedBlotterTable,
  useWSFilterPipe,
  useWsBlotterTable,
  type Position,
} from '@talos/kyoko';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { useMemo } from 'react';
import {
  defaultDerivativesBlotterColumns,
  defaultDerivativesBlotterFilter,
} from '../../../../Blotters/PositionsV3/defaults';
import { usePositionsDerivativesBlotterGrouping } from '../../../../Blotters/PositionsV3/Derivatives/usePositionsDerivativesBlotterGrouping';
import { usePositionsDerivativesColumns } from '../../../../Blotters/PositionsV3/Derivatives/usePositionsDerivativesColumns';
import {
  toDerivativesServerFilter,
  usePositionsDerivativesFilter,
} from '../../../../Blotters/PositionsV3/Derivatives/usePositionsDerivativesFilter';
import type { DerivativesPositionsTabProps } from '../../../../Blotters/PositionsV3/types';
import { usePositionsBaseRequest } from '../../../../Blotters/PositionsV3/usePositionsBaseRequest';
import { usePositionsExpiryFilter } from '../../../../Blotters/PositionsV3/usePositionsExpiryFilter';
import { MobileBlotterToolbar } from '../MobileBlotterToolbar';
import { MobilePositionsSettingsToggles } from './MobilePositionsSettingsToggles';
import { MobilePositionsBlotterView, getPositionAsset, getPositionKey } from './sharedPositionsLogic';

interface MobilePositionsDerivativesProps {
  blotterID: string;
  tab: DerivativesPositionsTabProps;
  onUpdateTab: (tab: DerivativesPositionsTabProps) => void;
}

/**
 * Mobile counterpart to desktop `PositionsDerivativesBlotter`. Mirrors the
 * desktop filtering pipeline on top of the shared positions WebSocket
 * subscription.
 */
export function MobilePositionsDerivatives({ blotterID, tab, onUpdateTab }: MobilePositionsDerivativesProps) {
  const defaultColumns = tab.defaultColumns ?? defaultDerivativesBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultDerivativesBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;

  const columns = usePositionsDerivativesColumns(defaultColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
    extraGridState: {
      rowGroupExpansion: { expansionState: 'all-expanded', expansionExceptions: [] },
    },
  });
  const filterResults = usePositionsDerivativesFilter({ persistedBlotterTable, defaultFilter });

  const expiryFilter = usePositionsExpiryFilter(getPositionAsset, filterResults.filter.Expiries);
  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionKey, filterFunc: expiryFilter });

  const baseRequest = usePositionsBaseRequest({ blotterID, showZeroBalances });
  const request = useMemo(
    () => ({
      ...baseRequest,
      ...removeEmptyFilters(toDerivativesServerFilter(filterResults.filter, filterResults.derivativesProductTypes)),
    }),
    [baseRequest, filterResults.derivativesProductTypes, filterResults.filter]
  );

  // Stable ref to latest grouping handler; defined after `useWsBlotterTable` (see `usePositionsDerivativesBlotterGrouping`).
  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const blotterTable = useWsBlotterTable({
    request,
    pipe: filterPipe,
    rowID: 'rowID',
    persistence: persistedBlotterTable,
    columns,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      showOpenedGroup: true,
      rowSelection: DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
    },
    exportOptions: { name: 'PositionsDerivatives', tabLabel: tab.label },
  });

  const { onGroupingStateUpdated, groupingToggleButtons } = usePositionsDerivativesBlotterGrouping({
    setRowGroupColumns: blotterTable.setRowGroupColumns,
    useMobileLabels: true,
    wrap: true,
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    filterBuilderProps: filterResults.filterBuilderProps,
  });

  return (
    <>
      <MobileBlotterToolbar
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={false}
        accordionBodyPrefix={groupingToggleButtons}
        settingsContent={<MobilePositionsSettingsToggles tab={tab} onUpdateTab={onUpdateTab} />}
      />
      <MobilePositionsBlotterView<Position> blotterTable={blotterTable} />
    </>
  );
}
